import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditIcon from '@mui/icons-material/Edit';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import PublicIcon from '@mui/icons-material/Public';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import TagIcon from '@mui/icons-material/Tag';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import DialogAdaptive from '../../../components/common/DialogAdaptive';
import StyledCardTitleBar from '../../../components/common/StyledCardTitleBar';
import StyledDialogActions from '../../../components/common/StyledDialogActions';
import MessageUsersFormEditiorPreview from '../../../components/services/messageUsers/sendMessage/MessageUsersFormEditiorPreview';
import type { ContentInventory } from '../../../hooks/useInventory';
import type { PpvMenuItem } from '../../../hooks/useMenu';
import type { OnlyFansVaultMedias } from '../../../hooks/useSubscriberVaultAlbums';

interface MenuItemCardProps {
    item: PpvMenuItem;
    contentInventory?: ContentInventory[];
    inventoryMediaById: Record<number, OnlyFansVaultMedias.RestructuredResponseItem>;
    activeLoading: boolean;
    deleteLoading: boolean;
    onEdit: (item: PpvMenuItem) => void;
    onToggleActive: (item: PpvMenuItem) => void;
    onDelete: (item: PpvMenuItem) => void;
}

const MenuItemCard = ({
    item,
    contentInventory,
    inventoryMediaById,
    activeLoading,
    deleteLoading,
    onEdit,
    onToggleActive,
    onDelete,
}: MenuItemCardProps) => {
    const theme = useTheme();
    const [previewOpen, setPreviewOpen] = useState(false);
    const formatLastTriggered = (value: string | null): string => {
        if (!value) return '—';
        const m = moment(value);
        if (!m.isValid()) return '—';
        return m.fromNow();
    };
    const formatExpireAt = (value: string | null): string => {
        if (!value) return 'No expiry';
        const m = moment(value);
        if (!m.isValid()) return 'No expiry';
        return m.fromNow();
    };
    const inventory = contentInventory?.find(entry => entry.inventoryId === item.inventoryId);
    const linkedVersion = inventory?.versions.find(version => version.inventorySubId === item.inventorySubId);
    const linkedTaggedCreators = (linkedVersion?.creatorTags ?? []).map(creator => ({
        id: creator.creatorOnlyfansId,
        name: creator.creatorName,
    }));
    const linkedPrice = typeof linkedVersion?.price === 'number' ? linkedVersion.price : Number(linkedVersion?.price || 0);
    const linkedLockedMedia = (linkedVersion?.lockedMedias ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(media => inventoryMediaById[media.mediaId])
        .filter(Boolean);
    const linkedUnlockedMediaIds = (linkedVersion?.unlockedMedias ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map(media => media.mediaId);

    return (
        <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <TagIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {item.command}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {item.public ? (
                                    <PublicIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                ) : (
                                    <LockOutlinedIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                                )}
                                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {item.public ? 'Public' : 'Private'}
                                </Typography>
                            </Stack>
                            <Chip size="small" label={item.active ? 'Active' : 'Inactive'} color={item.active ? 'success' : 'default'} />
                        </Stack>
                    </Stack>

                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {item.name}
                    </Typography>

                    <Grid container>
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            sx={{ py: 0.5, pr: { xs: 1, sm: 2 }, borderRight: theme => `1px solid ${theme.palette.divider}` }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <SendOutlinedIcon color="primary" />
                                <Box>
                                    <Typography variant="body1" color="text.secondary">
                                        Sent
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {item.sentCount}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            sm={4}
                            sx={{
                                py: 0.5,
                                px: { xs: 1, sm: 2 },
                                borderRight: {
                                    sm: theme => `1px solid ${theme.palette.divider}`,
                                    lg: `1px solid ${theme.palette.divider}`,
                                },
                            }}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <ShoppingBagOutlinedIcon color="primary" />
                                <Box>
                                    <Typography variant="body1" color="text.secondary">
                                        Purchased
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {item.purchaseCount}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4} sx={{ py: 0.5, pl: { sm: 2 } }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <AttachMoneyOutlinedIcon color="primary" />
                                <Box>
                                    <Typography variant="body1" color="text.secondary">
                                        Revenue
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        $
                                        {Number(item.totalPurchaseAmount || 0).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Divider sx={{ borderColor: 'divider', pt: 1 }} />

                    <Grid
                        container
                        spacing={0}
                        sx={{
                            py: 0.5,
                            pr: { xs: 1, sm: 2 },
                            // grid y spacing
                            '& > .MuiGrid-item': { py: 1 },
                        }}
                    >
                        <Grid item xs={12} sm={8}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <DescriptionOutlinedIcon color="action" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Template
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {inventory?.name || 'Missing Template'} - {linkedVersion?.versionName || 'Missing Version'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <AttachMoneyOutlinedIcon color="action" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Price
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        $
                                        {Number(linkedPrice || 0).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <ImageOutlinedIcon color="action" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Media
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {linkedLockedMedia.length} locked
                                        {linkedUnlockedMediaIds.length > 0 ? ` / ${linkedUnlockedMediaIds.length} unlocked` : ''}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <AccessTimeOutlinedIcon color="action" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Last triggered
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {formatLastTriggered(item.lastSentAt)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <EventOutlinedIcon color="action" />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Expires
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {formatExpireAt(item.expireAt)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>

                    <Divider sx={{ borderColor: 'divider' }} />

                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ pt: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button variant="outlined" startIcon={<PreviewOutlinedIcon />} onClick={() => setPreviewOpen(true)}>
                                Preview
                            </Button>
                            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(item)}>
                                Edit
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<PowerSettingsNewIcon />}
                                onClick={() => onToggleActive(item)}
                                disabled={activeLoading}
                            >
                                {item.active ? 'Pause' : 'Resume'}
                            </Button>
                        </Stack>
                        <Tooltip title="Delete">
                            <span>
                                <IconButton color="error" onClick={() => onDelete(item)} disabled={deleteLoading}>
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>
            </CardContent>
            {previewOpen && (
                <DialogAdaptive open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="sm">
                    <StyledCardTitleBar
                        title={`Message Preview - ${inventory?.name || 'Missing Template'} - ${
                            linkedVersion?.versionName || 'Missing Version'
                        }`}
                    />
                    <DialogContent sx={{ p: { xs: 1, sm: 2 } }}>
                        <MessageUsersFormEditiorPreview
                            message={linkedVersion?.text || item.message}
                            taggedCreators={linkedTaggedCreators}
                            taggedReleaseForms={linkedVersion?.additionalSettings?.consentForms || []}
                            mediaFiles={linkedLockedMedia}
                            mediaFilesPreviews={linkedUnlockedMediaIds}
                            lockMessage={linkedPrice > 0}
                            lockMessagePrice={linkedPrice > 0 ? linkedPrice : 0}
                            lockedText={false}
                            theme={theme}
                            fullWidth
                            showTags
                            showTime
                            showMedia
                        />
                    </DialogContent>
                    <StyledDialogActions>
                        <Button onClick={() => setPreviewOpen(false)} color="primary">
                            Close
                        </Button>
                    </StyledDialogActions>
                </DialogAdaptive>
            )}
        </Card>
    );
};

export default MenuItemCard;
